import { useMemo, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { humanizePrice } from '@/lib/humanizePrice';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import type { UserItem } from '@/types/userItem';
import { cn } from '@/lib/utils';
import { formatUsdCents } from '@/lib/formatMoney';

interface Row {
	id: string;
	title: string;
	fromCents: number;
	toCents: number;
	deltaPct: number;
	reason: string;
	included: boolean;
}

interface Props {
	open: boolean;
	onOpenChange: (o: boolean) => void;
	items: UserItem[];
	onDone?: () => void;
}

/**
 * Suggest Prices — computes charm-humanized prices for every priced item in
 * the current selection and lets the seller review row-by-row before applying.
 * Uses the same ±2% drift-capped engine as the admin humanize console; simply
 * exposes it as a bulk seller action.
 */
export function SuggestPricesReviewDialog({ open, onOpenChange, items, onDone }: Props) {
	const qc = useQueryClient();
	const { user } = useAuth();
	const [applying, setApplying] = useState(false);
	const [overrides, setOverrides] = useState<Record<string, boolean>>({});

	const suggestions = useMemo<Row[]>(() => {
		return items
			.filter((it) => (it.asking_price_cents ?? 0) > 0)
			.map((it) => {
				const fromCents = it.asking_price_cents ?? 0;
				const res = humanizePrice({
					price: fromCents / 100,
					product_type: (it as any).product_type ?? null,
					title: it.title ?? null,
				});
				const toCents = Math.round(res.suggested * 100);
				return {
					id: it.id,
					title: it.title || 'Untitled',
					fromCents,
					toCents,
					deltaPct: res.deltaPct,
					reason: res.reason,
					included: res.changed,
				};
			})
			.filter((r) => r.fromCents !== r.toCents);
	}, [items]);

	const rows = suggestions.map((s) => ({
		...s,
		included: overrides[s.id] ?? s.included,
	}));
	const includedRows = rows.filter((r) => r.included);
	const changedCount = includedRows.length;

	const apply = async () => {
		if (!user || includedRows.length === 0) return;
		setApplying(true);
		let ok = 0;
		let fail = 0;
		for (const r of includedRows) {
			const { error } = await supabase
				.from('user_items')
				.update({ asking_price_cents: r.toCents } as any)
				.eq('id', r.id)
				.eq('user_id', user.id);
			if (error) fail += 1;
			else ok += 1;
		}
		setApplying(false);
		if (ok > 0) toast.success(`${ok} price${ok === 1 ? '' : 's'} updated`);
		if (fail > 0) toast.error(`${fail} update${fail === 1 ? '' : 's'} failed`);
		qc.invalidateQueries({ queryKey: ['my-items'] });
		onOpenChange(false);
		onDone?.();
	};

	const fmt = (c: number) => `${formatUsdCents(c)}`;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-xl">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Sparkles className="w-4 h-4 text-active-gold" />
						Suggest prices
					</DialogTitle>
					<DialogDescription>
						Charm endings, drift capped at ±2%. Only priced items are shown; unpriced items are skipped.
					</DialogDescription>
				</DialogHeader>

				{rows.length === 0 ? (
					<div className="py-8 text-center text-sm text-muted-foreground">
						{items.length === 0
							? 'No items selected.'
							: 'Nothing to suggest — every selected price is already clean.'}
					</div>
				) : (
					<div className="max-h-[55dvh] overflow-y-auto space-y-1.5 pr-1">
						{rows.map((r) => (
							<label
								key={r.id}
								className={cn(
									'flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer transition-colors',
									r.included
										? 'bg-active-gold/[0.06] border-active-gold/30'
										: 'bg-white/[0.02] border-white/[0.06]',
								)}
							>
								<input
									type="checkbox"
									checked={r.included}
									onChange={(e) =>
										setOverrides((o) => ({ ...o, [r.id]: e.target.checked }))
									}
									className="w-4 h-4 accent-active-gold shrink-0"
								/>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">{r.title}</p>
									<p className="text-[11px] text-muted-foreground">{r.reason}</p>
								</div>
								<div className="text-right shrink-0 text-sm tabular-nums">
									<span className="text-muted-foreground line-through">{fmt(r.fromCents)}</span>
									<span className="mx-1.5 text-muted-foreground">→</span>
									<span className="text-active-gold font-semibold">{fmt(r.toCents)}</span>
									<div className="text-[10px] text-muted-foreground mt-0.5">
										{(r.deltaPct * 100).toFixed(1)}%
									</div>
								</div>
							</label>
						))}
					</div>
				)}

				<div className="flex items-center justify-end gap-2 pt-2">
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
						Cancel
					</Button>
					<Button onClick={apply} disabled={applying || changedCount === 0} className="gap-2">
						{applying ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Applying…
							</>
						) : (
							`Apply ${changedCount || ''} update${changedCount === 1 ? '' : 's'}`
						)}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
